import { MantineProvider, createTheme } from '@mantine/core';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import { MedplumClient } from '@medplum/core';
import { MedplumProvider } from '@medplum/react';
import '@medplum/react/styles.css';
import { Notifications } from '@mantine/notifications';
import '@mantine/notifications/styles.css';
import '@mantine/tiptap/styles.css';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import { App } from './App';
import { ConfigService } from '@services/config.service';
import * as Sentry from '@sentry/react';
import './i18n';
import { EnvEnum } from './types/env.enum';
import { AccessPolicyProvider } from './providers/AccessPolicyProvider';

// Only initialize Sentry for non-development environments
if (ConfigService.getEnvironment() !== EnvEnum.Development) {
  Sentry.init({
    dsn: ConfigService.getSentryDsn(),
    environment: ConfigService.getEnvironment(),
    release: ConfigService.getCommitHash(),
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
  });
}

const medplum = new MedplumClient({
  onUnauthenticated: () => (window.location.href = '/'),
  baseUrl: ConfigService.getApiUrl(),
  cacheTime: 60000,
  autoBatchTime: 100,
});

const theme = createTheme({
  headings: {
    sizes: {
      h1: {
        fontSize: '1.125rem',
        fontWeight: '500',
        lineHeight: '2.0',
      },
    },
  },
  fontSizes: {
    xs: '0.6875rem',
    sm: '0.875rem',
    md: '0.875rem',
    lg: '1.0rem',
    xl: '1.125rem',
  },
  primaryColor: 'teal',
});

const router = createBrowserRouter([{ path: '*', element: <App /> }]);

const navigate = (path: string): Promise<void> => router.navigate(path);

const container = document.getElementById('root') as HTMLDivElement;
const root = createRoot(container);
root.render(
  <StrictMode>
    <MedplumProvider medplum={medplum} navigate={navigate}>
      <MantineProvider theme={theme}>
        <AccessPolicyProvider>
          <Notifications position="bottom-right" />
          <RouterProvider router={router} />
        </AccessPolicyProvider>
      </MantineProvider>
    </MedplumProvider>
  </StrictMode>
);
